import Service, { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import Ember from 'ember';
import { task } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';

import ApiService from 'cio/services/api';
import AccountService from 'cio/services/account';
import config from 'cio/config/environment';
import HydraService from './hydra';

// 1 minute:
const FETCH_INTERVAL = 1 * 60 * 1000;

export interface ReleaseVersionData {
  major: string;
  minor: string;
  patch: string;
  commit: string;
  version: string;
  createdAt: number;
}

export default class ReleaseNotificationService extends Service {
  @service api!: ApiService;
  @service account!: AccountService;
  @service hydra!: HydraService;

  original = this.parseVersion(config.APP.version, config.APP.buildTimestamp);
  current!: ReleaseVersionData;
  showModal = false;
  wasMajorUpgradeModalAlreadyShown = false;

  fetch = task({ drop: true }, async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let request;
      if (this.hydra.isHydraEnabled) {
        // TODO: Make this into a new pathScope once we remove the /cdp prefix
        request = this.api.request('cdp/hydra/release_summary', {
          pathScope: 'global',
          removeUrlNamespace: true,
          ignoreErrors: true,
        });
      } else {
        request = this.api.request('releases/current', {
          pathScope: 'global',
          ignoreErrors: true,
        });
      }

      try {
        let response = await request;
        let release = null;

        if (this.hydra.isHydraEnabled) {
          release = response.journeys;
        } else {
          release = response.release;
        }

        let result = this.parseVersion(release.version, release.created_at);

        this.set('current', result);

        // show major upgrade modal only if it wasn't already dismissed: due to internal browser feature preventing window refresh with unsaved changes (e.g. when editing a form)
        if (!this.wasMajorUpgradeModalAlreadyShown) {
          this.checkForMajorVersion();
        }
      } catch (e) {
        // Ignore API failures
      }

      if (Ember.testing) {
        // Can't have infinite loops with timeouts in acceptance tests
        break;
      }

      await timeout(FETCH_INTERVAL);
    }
  });

  private checkForMajorVersion(): void {
    if (this.current.major !== this.original.major) {
      this.set('wasMajorUpgradeModalAlreadyShown', true);
      this.set('showModal', true);
    }
  }

  private parseVersion(version: string, createdAt: number): ReleaseVersionData {
    let [major, minor, patch, commit] = version.split(/[\.\+]/);

    return {
      major,
      minor,
      patch,
      commit,
      version,
      createdAt,
    };
  }

  checkOnRouteChange = task({ drop: true }, async () => {
    if (Ember.testing) {
      // Since fetch doesn't loop in acceptance tests, force fetch before check
      await this.fetch.perform();
    }

    let { original, current } = this;

    if (isNone(current)) {
      return;
    }

    // also check for major version: handles scenario where original modal was dismissed due to internal browser feature preventing window refresh with unsaved changes (e.g. when editing a form)
    if (current.major !== original.major || current.minor !== original.minor) {
      this.set('showModal', true);
    }
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'release-notification': ReleaseNotificationService;
  }
}
